import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useLoadScripts from '../utils/useLoadScript';
import { MetaTagComponent, MetaTags } from '../utils/MetaTags';

function PrivacyPage() {
  useLoadScripts([
    '/assets/js/jquery-3.7.1.min.js',
    '/assets/js/jquery-ui.js',
    '/assets/js/moment.min.js',
    '/assets/js/bootstrap.min.js',
    '/assets/js/swiper-bundle.min.js',
    '/assets/js/waypoints.js',
    '/assets/js/jquery.counterup.min.js',
    '/assets/js/wow.min.js',
    '/assets/js/jquery.nice-select.min.js',
    '/assets/js/jquery.marquee.min.js',
    '/assets/js/gsap.min.js',
    '/assets/js/ScrollTrigger.min.js',
    '/assets/js/ScrollSmoother.min.js',
    '/assets/js/SplitText.min.js',
    '/assets/js/jquery.fancybox.min.js',
    '/assets/js/three.js',
    '/assets/js/hover-effect.umd.js',
    '/assets/js/custom.js',
  ]);

  // Premium Styles
  const containerStyle = {
    maxWidth: "900px",
    margin: "120px auto",
    padding: "60px",
    fontFamily: "'Inter', 'Poppins', sans-serif",
    backgroundColor: "#ffffff",
    borderRadius: "24px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.04)",
    border: "1px solid #f0f0f0",
  };

  const sectionStyle = {
    marginBottom: "40px",
    paddingBottom: "20px",
    borderBottom: "1px solid #f9f9f9",
  };

  const headingStyle = {
    fontSize: "42px",
    fontWeight: "700",
    letterSpacing: "-1px",
    marginBottom: "20px",
    color: "#111",
    textAlign: "center"
  };

  const subHeadingStyle = {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "16px",
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
  };

  const textStyle = {
    fontSize: "16px",
    lineHeight: "1.8",
    color: "#4b5563",
    fontWeight: "400",
  };

  const listStyle = {
    ...textStyle,
    paddingLeft: "25px",
    marginTop: "10px",
  };

  const badgeStyle = {
    display: "inline-block",
    padding: "6px 16px",
    backgroundColor: "#f3f4f6",
    borderRadius: "50px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#6b7280",
    marginBottom: "20px",
    textTransform: "uppercase",
    letterSpacing: "1px"
  };

  return (
    <div id="smooth-wrapper">
      <div id="smooth-content">
        <MetaTagComponent 
          pageTitle={MetaTags.privacy.title}
          pageDescription={MetaTags.privacy.description}
          pageKeywords={MetaTags.privacy.keywords}
        />
        <Header />

        <main style={{ backgroundColor: "#fafafa", overflow: "hidden", padding: "20px" }}>
          <div style={containerStyle} className="wow fadeInUp" data-wow-delay="0.2s">
            
            <div style={{ textAlign: "center", marginBottom: "50px" }}>
              <span style={badgeStyle}>Legal Compliance</span>
              <h1 style={headingStyle}>Privacy Policy</h1>
              <p style={{ ...textStyle, fontSize: "18px", color: "#9ca3af" }}>
                Effective Date: April 2026
              </p>
            </div>

            <div style={sectionStyle}>
              <p style={textStyle}>
                Welcome to <strong>InsightWide</strong>. Your privacy is important to us, and we are committed
                to protecting the personal information you share with us. This Privacy Policy explains how
                InsightWide, its affiliates, and related entities (collectively referred to as "InsightWide",
                "we", "our", or "us") collect, use, store, and protect your information when you use our
                website, products, and services.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>1. Information We Collect</h2>
              <p style={textStyle}>
                We may collect personal and business information including, but not limited to:
              </p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}>Full name</li>
                <li style={{ marginBottom: "8px" }}>Company name</li>
                <li style={{ marginBottom: "8px" }}>Email address</li>
                <li style={{ marginBottom: "8px" }}>Phone number</li>
                <li style={{ marginBottom: "8px" }}>Billing and payment details</li>
                <li style={{ marginBottom: "8px" }}>Account login credentials</li>
                <li style={{ marginBottom: "8px" }}>Website usage and analytics data</li>
                <li style={{ marginBottom: "8px" }}>IP address and device information</li>
                <li style={{ marginBottom: "8px" }}>Customer support communications</li>
              </ul>
              <p style={{ ...textStyle, marginTop: "16px" }}>We collect this information when you:</p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}>Visit our website</li>
                <li style={{ marginBottom: "8px" }}>Register for an account</li>
                <li style={{ marginBottom: "8px" }}>Contact our team</li>
                <li style={{ marginBottom: "8px" }}>Request a demo or support</li>
                <li style={{ marginBottom: "8px" }}>Subscribe to newsletters or marketing communications</li>
                <li style={{ marginBottom: "8px" }}>Use our platform and services</li>
              </ul>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>2. How We Use Your Information</h2>
              <p style={textStyle}>InsightWide uses your information to:</p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}>Provide and maintain our services</li>
                <li style={{ marginBottom: "8px" }}>Manage customer accounts and subscriptions</li>
                <li style={{ marginBottom: "8px" }}>Improve platform functionality and user experience</li>
                <li style={{ marginBottom: "8px" }}>Respond to inquiries and provide support</li>
                <li style={{ marginBottom: "8px" }}>Send service updates and important notifications</li>
                <li style={{ marginBottom: "8px" }}>Analyze website and platform performance</li>
                <li style={{ marginBottom: "8px" }}>Improve business operations and security</li>
                <li style={{ marginBottom: "8px" }}>Comply with legal and regulatory obligations</li>
              </ul>
              <p style={{ ...textStyle, marginTop: "16px" }}>
                We only collect information that is reasonably necessary to deliver our services effectively.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>3. Sharing of Information</h2>
              <p style={textStyle}>We may share your information with:</p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}>Trusted third-party service providers</li>
                <li style={{ marginBottom: "8px" }}>Cloud hosting and infrastructure providers</li>
                <li style={{ marginBottom: "8px" }}>Payment processing partners</li>
                <li style={{ marginBottom: "8px" }}>CRM and analytics platforms</li>
                <li style={{ marginBottom: "8px" }}>Legal or regulatory authorities when required by law</li>
              </ul>
              <p style={{ ...textStyle, marginTop: "16px" }}>
                All third-party providers are required to maintain appropriate confidentiality and security
                standards. We do not sell, rent, or trade your personal information to third parties.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>4. Data Storage and Security</h2>
              <p style={textStyle}>
                InsightWide implements industry-standard security measures to protect your information from
                unauthorized access, misuse, loss, or disclosure. These measures include:
              </p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}><strong>AES-256 encryption</strong></li>
                <li style={{ marginBottom: "8px" }}>Secure cloud infrastructure</li>
                <li style={{ marginBottom: "8px" }}>Firewall and network protection</li>
                <li style={{ marginBottom: "8px" }}>Role-based access controls</li>
                <li style={{ marginBottom: "8px" }}>Regular monitoring and security updates</li>
              </ul>
              <p style={{ ...textStyle, marginTop: "16px" }}>
                While we take reasonable steps to secure your information, no method of electronic
                transmission or storage is completely secure.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>5. Cookies and Analytics</h2>
              <p style={textStyle}>
                Our website may use cookies and similar technologies to improve user experience, analyze
                traffic, and enhance website functionality. Cookies may collect information such as:
              </p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}>Browser type</li>
                <li style={{ marginBottom: "8px" }}>Device information</li>
                <li style={{ marginBottom: "8px" }}>Pages visited</li>
                <li style={{ marginBottom: "8px" }}>Session duration</li>
                <li style={{ marginBottom: "8px" }}>Website preferences</li>
              </ul>
              <p style={{ ...textStyle, marginTop: "16px" }}>
                You can manage or disable cookies through your browser settings.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>6. International Data Transfers</h2>
              <p style={textStyle}>
                Your information may be processed or stored in countries outside Australia. Where
                international transfers occur, we ensure that appropriate safeguards and data protection
                measures are in place.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>7. Your Rights</h2>
              <p style={textStyle}>
                Depending on applicable privacy laws, you may have the right to:
              </p>
              <ul style={listStyle}>
                <li style={{ marginBottom: "8px" }}>Access your personal information</li>
                <li style={{ marginBottom: "8px" }}>Request correction of inaccurate data</li>
                <li style={{ marginBottom: "8px" }}>Request deletion of your information</li>
                <li style={{ marginBottom: "8px" }}>Withdraw consent where applicable</li>
                <li style={{ marginBottom: "8px" }}>Object to certain processing activities</li>
                <li style={{ marginBottom: "8px" }}>Request a copy of your stored information</li>
              </ul>
              <p style={{ ...textStyle, marginTop: "16px" }}>
                To exercise your rights, please contact us using the details below.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>8. Third-Party Links</h2>
              <p style={textStyle}>
                Our website or platform may contain links to third-party websites or services. InsightWide
                is not responsible for the privacy practices or content of external websites.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>9. Retention of Information</h2>
              <p style={textStyle}>
                We retain personal information only for as long as necessary to fulfill the purposes
                outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and
                enforce agreements.
              </p>
            </div>

            <div style={sectionStyle}>
              <h2 style={subHeadingStyle}>10. Updates to This Privacy Policy</h2>
              <p style={textStyle}>
                InsightWide may update this Privacy Policy from time to time to reflect changes in legal
                requirements, technology, or business operations. Updated versions will be published on
                this page with the revised effective date.
              </p>
            </div>

            <div style={{ ...sectionStyle, borderBottom: "none", marginBottom: 0 }}>
              <h2 style={subHeadingStyle}>11. Contact Us</h2>
              <p style={textStyle}>
                If you have questions, concerns, or requests regarding this Privacy Policy or your
                personal information, please contact us:
              </p>
              <p style={{ ...textStyle, marginTop: "16px" }}>
                <strong>InsightWide</strong><br />
                A Product of Teknoverse<br />
                Melbourne, Victoria, Australia<br />
                Email: <a href="mailto:support@insightwide.com.au" style={{ color: "#4b5563" }}>support@insightwide.com.au</a><br />
                Website: <a href="https://insightwide.com.au" style={{ color: "#4b5563" }}>https://insightwide.com.au</a>
              </p>
            </div>

          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default PrivacyPage;